import { useEffect, useEffectEvent, useRef } from 'react'
import cx from 'classnames'
import {
  acceptCompletion,
  autocompletion,
  type CompletionContext,
  completionKeymap,
  startCompletion,
} from '@codemirror/autocomplete'
import {
  copyLineDown,
  cursorMatchingBracket,
  defaultKeymap,
  history,
  historyKeymap,
  indentLess,
  indentMore,
  moveLineDown,
  moveLineUp,
  selectLine,
} from '@codemirror/commands'
import {
  HighlightStyle,
  StreamLanguage,
  syntaxHighlighting,
} from '@codemirror/language'
import { coffeeScript } from '@codemirror/legacy-modes/mode/coffeescript'
import { type Diagnostic, lintGutter, setDiagnostics } from '@codemirror/lint'
import { selectNextOccurrence } from '@codemirror/search'
import { Compartment, EditorState, type StateCommand } from '@codemirror/state'
import {
  drawSelection,
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  placeholder as placeholderExtension,
  rectangularSelection,
} from '@codemirror/view'
import { tags } from '@lezer/highlight'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Types } from '@/features/legends/editor/ryno-types'
import {
  filterCompletionNames,
  findCompletionWord,
} from '@/features/legends/ryno/ryno-completions'
import type {
  RynoCompletion,
  RynoDiagnostic,
} from '@/features/legends/ryno/RynoEditorViewModel'
import styles from './RynoEditor.module.css'

export interface RynoEditorProps {
  value: string
  onChange: (value: string) => unknown
  /**
   * The diagnostics in the order they appear in the formula.
   */
  diagnostics: readonly RynoDiagnostic[]
  /**
   * The identifiers in scope, listed by Ctrl+Space.
   */
  completions: readonly RynoCompletion[]
  isInvalid?: boolean
  isDisabled?: boolean
  placeholder?: string
  /**
   * A shorter editor, for tight spots.
   */
  isCompact?: boolean
  'aria-label'?: string
  testId?: string
}

const highlightStyle = HighlightStyle.define([
  { tag: tags.comment, color: 'var(--ryno-comment)', fontStyle: 'italic' },
  {
    tag: [tags.string, tags.special(tags.string)],
    color: 'var(--ryno-string)',
  },
  { tag: [tags.number, tags.bool, tags.null], color: 'var(--ryno-number)' },
  { tag: [tags.keyword, tags.operatorKeyword], color: 'var(--ryno-keyword)' },
  {
    tag: [tags.operator, tags.punctuation, tags.propertyName],
    color: 'var(--ryno-operator)',
  },
  { tag: tags.variableName, color: 'var(--ryno-variable)' },
  {
    tag: [tags.atom, tags.definition(tags.variableName)],
    color: 'var(--ryno-atom)',
  },
])

/**
 * Sublime Text bindings that CodeMirror's defaults lack.
 */
const sublimeKeymap = [
  { key: 'Mod-d', run: selectNextOccurrence, preventDefault: true },
  { key: 'Mod-l', run: selectLine },
  { key: 'Shift-Mod-d', run: copyLineDown },
  { key: 'Shift-Mod-ArrowUp', mac: 'Ctrl-Mod-ArrowUp', run: moveLineUp },
  { key: 'Shift-Mod-ArrowDown', mac: 'Ctrl-Mod-ArrowDown', run: moveLineDown },
  { key: 'Ctrl-m', run: cursorMatchingBracket },
]

/**
 * Inserts two spaces at the cursor, or indents the selected lines. Not
 * `insertTab`: the diagnostics are computed on the formula with tabs as two
 * spaces, so a tab would shift their marks.
 */
const insertIndent: StateCommand = ({ state, dispatch }) => {
  if (state.selection.ranges.some((r) => !r.empty)) {
    return indentMore({ state, dispatch })
  }
  dispatch(
    state.update(state.replaceSelection('  '), {
      scrollIntoView: true,
      userEvent: 'input',
    }),
  )
  return true
}

const editableCompartment = new Compartment()

/**
 * The Ryno formula editor. Ctrl+Space lists the symbols in scope.
 */
export function RynoEditor({
  value,
  onChange,
  diagnostics,
  completions,
  isInvalid,
  isDisabled,
  placeholder = 'Enter a Ryno formula…',
  isCompact,
  'aria-label': ariaLabel,
  testId,
}: RynoEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const viewRef = useRef<EditorView | null>(null)

  const emitChange = useEffectEvent((text: string) => onChange(text))
  const getCompletions = useEffectEvent(() => completions)
  // The view is created once. Later changes to `value` and `isDisabled` are
  // applied by the effects below; the placeholder and label are fixed.
  const getInitialState = useEffectEvent(() => ({
    value,
    isDisabled,
    placeholder,
    ariaLabel,
  }))

  useEffect(() => {
    const host = hostRef.current
    if (!host) {
      return
    }

    const initial = getInitialState()
    const complete = (context: CompletionContext) => {
      const line = context.state.doc.lineAt(context.pos)
      const word = findCompletionWord(line.text, context.pos - line.from)
      if (!word.typed && !context.explicit) {
        return null
      }
      return {
        from: line.from + word.from,
        to: line.from + word.to,
        options: filterCompletionNames(getCompletions(), word.typed).map(
          (symbol) => ({
            label: symbol.name,
            detail: Types.describeType(symbol.type),
            type: 'variable',
          }),
        ),
        filter: false,
      }
    }

    const view = new EditorView({
      parent: host,
      state: EditorState.create({
        doc: initial.value,
        extensions: [
          lineNumbers(),
          highlightActiveLineGutter(),
          highlightActiveLine(),
          history(),
          drawSelection(),
          EditorState.allowMultipleSelections.of(true),
          rectangularSelection(),
          StreamLanguage.define(coffeeScript),
          syntaxHighlighting(highlightStyle),
          autocompletion({
            override: [complete],
            activateOnTyping: false,
            icons: false,
            tooltipClass: () => styles.completions,
          }),
          lintGutter(),
          keymap.of([
            { key: 'Ctrl-Space', run: startCompletion },
            ...completionKeymap,
            ...sublimeKeymap,
            ...defaultKeymap,
            ...historyKeymap,
            { key: 'Tab', run: acceptCompletion },
            { key: 'Tab', run: insertIndent, shift: indentLess },
          ]),
          EditorState.tabSize.of(2),
          placeholderExtension(initial.placeholder),
          EditorView.contentAttributes.of(
            initial.ariaLabel ? { 'aria-label': initial.ariaLabel } : {},
          ),
          editableCompartment.of(editability(initial.isDisabled)),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              emitChange(update.state.doc.toString())
            }
          }),
        ],
      }),
    })
    viewRef.current = view

    return () => {
      view.destroy()
      viewRef.current = null
    }
  }, [])

  // Follows changes made outside the editor, like inserting a template.
  useEffect(() => {
    const view = viewRef.current
    const current = view?.state.doc.toString()
    if (view && value !== current) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
      })
    }
  }, [value])

  useEffect(() => {
    const view = viewRef.current
    view?.dispatch(
      setDiagnostics(
        view.state,
        toLintDiagnostics(diagnostics, view.state.doc.length),
      ),
    )
  }, [diagnostics])

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: editableCompartment.reconfigure(editability(isDisabled)),
    })
  }, [isDisabled])

  const selectDiagnostic = (diagnostic: RynoDiagnostic) => {
    const view = viewRef.current
    if (
      !view ||
      diagnostic.startPos === undefined ||
      diagnostic.endPos === undefined
    ) {
      return
    }
    const length = view.state.doc.length
    view.dispatch({
      selection: {
        anchor: Math.min(diagnostic.startPos, length),
        head: Math.min(diagnostic.endPos, length),
      },
      scrollIntoView: true,
    })
    view.focus()
  }

  return (
    <div className={styles.root} data-test={testId}>
      <div
        className={cx(
          styles.control,
          isCompact && styles.compact,
          isDisabled && styles.disabled,
          isInvalid && styles.invalid,
        )}
      >
        <div ref={hostRef} className={styles.host} />
        {!isDisabled &&
          (diagnostics.length === 0 ? (
            <div className={cx(styles.diagnostics, styles.clean)}>
              <div className={styles.diagnostic}>
                <Icon name="status-check" size={13} aria-hidden="true" />
                <span className={styles.message}>This looks good to go.</span>
              </div>
            </div>
          ) : (
            <div className={styles.diagnostics}>
              {diagnostics.map((diagnostic, i) => (
                <RawButton
                  key={i}
                  className={cx(
                    styles.diagnostic,
                    styles.diagnosticButton,
                    diagnostic.severity === 'warning' && styles.warning,
                  )}
                  onPress={() => selectDiagnostic(diagnostic)}
                >
                  <Icon
                    name={
                      diagnostic.severity === 'error' ? 'status-alert' : 'alert'
                    }
                    size={13}
                    aria-hidden="true"
                  />
                  <span className={styles.message}>{diagnostic.message}</span>
                  {diagnostic.loc && (
                    <span className={styles.location}>
                      {diagnostic.loc.start.line}:{diagnostic.loc.start.col}
                    </span>
                  )}
                </RawButton>
              ))}
            </div>
          ))}
      </div>
    </div>
  )
}

function editability(isDisabled: boolean | undefined) {
  return [
    EditorView.editable.of(!isDisabled),
    EditorState.readOnly.of(!!isDisabled),
  ]
}

function toLintDiagnostics(
  diagnostics: readonly RynoDiagnostic[],
  docLength: number,
): Diagnostic[] {
  return diagnostics.flatMap((d) => {
    if (d.startPos === undefined) {
      return []
    }
    const from = Math.min(d.startPos, docLength)
    const to = Math.min(Math.max(d.endPos ?? from, from), docLength)
    return [{ from, to, severity: d.severity, message: d.message }]
  })
}
